import { IconMessageChatbot, IconSend, IconSparkles, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../api";
import { useSession } from "../session";

interface Turn { role: "user" | "assistant"; text: string; handedOver?: boolean }

const KEEP = 20;
const storageKey = (brand: string) => `assistantChat:${brand}`;

function loadTurns(brand: string): Turn[] {
  try {
    const raw = sessionStorage.getItem(storageKey(brand));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is Turn => typeof x === "object" && x !== null && "role" in x && "text" in x && typeof (x as Turn).text === "string");
  } catch {
    return [];
  }
}

function saveTurns(brand: string, turns: Turn[]) {
  try {
    sessionStorage.setItem(storageKey(brand), JSON.stringify(turns.slice(-KEEP)));
  } catch {
    // Storage blocked: the exchange lasts while the page does.
  }
}

/** The floating assistant (docs/16 §2.5): instant answers, labelled as the assistant, with a hand-over to the consultant on request. */
export function AssistantBubble() {
  const { brand, t } = useSession();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>(() => (brand ? loadTurns(brand.code) : []));
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (brand) saveTurns(brand.code, turns);
  }, [turns, brand]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: "end" });
  }, [open, turns.length, busy]);

  if (!brand) return null;

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    const asked: Turn = { role: "user", text: q };
    setTurns((prev) => [...prev, asked].slice(-KEEP));
    setText("");
    try {
      const res = await api.askAssistant(q);
      const answer = res.answer?.body ?? (res.handedOver ? t("consultantTold") : t("somethingWrong"));
      const reply: Turn = { role: "assistant", text: answer, handedOver: res.handedOver };
      setTurns((prev) => [...prev, reply].slice(-KEEP));
    } catch {
      setError(t("somethingWrong"));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void ask(text);
  };

  const chips: string[] = [t("chipDocuments"), t("chipTomorrow"), t("chipPerson")];

  return (
    <>
      <button type="button" className="fab" onClick={() => setOpen(true)} aria-label={t("assistantOpen")} title={t("assistantOpen")}>
        <IconMessageChatbot size={26} stroke={1.75} />
      </button>
      {open && (
        <div className="sheet-backdrop" onClick={() => setOpen(false)}>
          <div className="sheet sheet-assistant" role="dialog" aria-modal="true" aria-label={t("assistantOpen")} onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <div>
                <h2 className="assistant-title">
                  <IconSparkles size={18} /> {t("assistant")} · {brand.shortName}
                </h2>
                <p className="muted small">{t("assistantNote")}</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setOpen(false)} aria-label={t("close")}>
                <IconX size={18} />
              </button>
            </div>

            <div className="assistant-log">
              {turns.length === 0 && <p className="muted small">{t("assistantEmpty")}</p>}
              {turns.map((turn, i) => (
                <div key={i} className={`bubble-row bubble-${turn.role === "user" ? "right" : "left"}`}>
                  <div className={`bubble ${turn.role === "user" ? "bubble-customer" : "bubble-assistant"}`}>
                    {turn.role === "assistant" && (
                      <span className="bubble-label">
                        <IconSparkles size={12} /> {t("assistant")}
                      </span>
                    )}
                    <p className="bubble-body">{turn.text}</p>
                    {turn.handedOver && <span className="bubble-handoff">{t("consultantTold")}</span>}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="bubble-row bubble-left">
                  <div className="bubble bubble-assistant">
                    <span className="bubble-label">
                      <IconSparkles size={12} /> {t("assistant")}
                    </span>
                    <p className="bubble-body muted">{t("assistantThinking")}</p>
                  </div>
                </div>
              )}
              {error && <p className="error-note small">{error}</p>}
              <div ref={endRef} />
            </div>

            <div className="assistant-chips">
              {chips.map((chip) => (
                <button key={chip} type="button" className="chip chip-button" disabled={busy} onClick={() => void ask(chip)}>
                  {chip}
                </button>
              ))}
            </div>

            <form className="composer-row" onSubmit={submit}>
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("assistantPlaceholder")} maxLength={4000} aria-label={t("assistantPlaceholder")} />
              <button type="submit" className="button button-primary button-small" disabled={busy || !text.trim()} aria-label={t("send")}>
                <IconSend size={16} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
